import { useState, useEffect } from 'react';
import axios from 'axios';
import { useFilters } from '../contexts/FilterContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function DocumentsPage() {
  const filters = useFilters();
  const [timePeriod, setTimePeriod] = useState<'monthly' | 'quarterly' | 'annual'>('quarterly');
  const [institutions, setInstitutions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/institutions`);
        setInstitutions(response.data);
      } catch (error) {
        console.error('Failed to fetch institutions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstitutions();
  }, []);

  // Filter institutions based on context
  const filteredInstitutions = institutions.filter((inst) => {
    if (filters.selectedInstitution !== 'all' && inst.institution_id !== filters.selectedInstitution) {
      return false;
    }
    if (filters.selectedRegion !== 'all' && inst.region !== filters.selectedRegion) {
      return false;
    }
    if (filters.selectedType !== 'all' && inst.type !== filters.selectedType) {
      return false;
    }
    if (filters.selectedTier !== 'all' && inst.tier !== filters.selectedTier) {
      return false;
    }
    return true;
  });

  // Calculate overall statistics
  const calculateStats = () => {
    const stats = {
      totalReturns: 0,
      onTime: 0,
      late: 0,
      pending: 0,
    };

    filteredInstitutions.forEach((inst) => {
      if (inst.returns_compliance) {
        Object.values(inst.returns_compliance).forEach((returnData: any) => {
          stats.totalReturns++;
          if (returnData.status === 'On Time') stats.onTime++;
          else if (returnData.status === 'Late') stats.late++;
          else if (returnData.status === 'Pending') stats.pending++;
        });
      }
    });

    return stats;
  };

  const stats = calculateStats();
  const completionRate = stats.totalReturns > 0 ? ((stats.onTime / stats.totalReturns) * 100).toFixed(1) : '0';

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-sm text-gray-600">Regulatory Returns Submission Tracking</p>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              parseFloat(completionRate) >= 95 ? 'bg-green-100 text-green-800' :
              parseFloat(completionRate) >= 80 ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {completionRate}% On-Time Submission Rate
            </span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
              {filteredInstitutions.length} Institutions Tracked
            </span>
          </div>

          {/* Time Period Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">View:</span>
            <button
              onClick={() => setTimePeriod('monthly')}
              className={`px-3 py-1 rounded text-sm font-medium ${
                timePeriod === 'monthly'
                  ? 'bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setTimePeriod('quarterly')}
              className={`px-3 py-1 rounded text-sm font-medium ${
                timePeriod === 'quarterly'
                  ? 'bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Quarterly
            </button>
            <button
              onClick={() => setTimePeriod('annual')}
              className={`px-3 py-1 rounded text-sm font-medium ${
                timePeriod === 'annual'
                  ? 'bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Annual
            </button>
          </div>
        </div>
      </div>

      {/* Processing Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Returns"
          value={stats.totalReturns}
          subtitle={`Tracked ${timePeriod}`}
          icon="📊"
          color="blue"
        />
        <SummaryCard
          title="On Time"
          value={stats.onTime}
          subtitle="Meeting deadlines"
          icon="✓"
          color="green"
        />
        <SummaryCard
          title="Late"
          value={stats.late}
          subtitle="Past due date"
          icon="⚠️"
          color="red"
        />
        <SummaryCard
          title="Pending"
          value={stats.pending}
          subtitle="Awaiting submission"
          icon="⏳"
          color="yellow"
        />
      </div>

      {/* Returns Submission Status by Institution */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Institution Returns Compliance Status
        </h3>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Institution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Return Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  YTD Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInstitutions.map((inst) => {
                if (!inst.returns_compliance) return null;

                return Object.entries(inst.returns_compliance).map(([returnKey, returnData]: [string, any]) => {
                  // Filter by time period
                  if (timePeriod === 'monthly' && returnData.frequency !== 'Monthly') return null;
                  if (timePeriod === 'quarterly' && returnData.frequency !== 'Quarterly') return null;
                  if (timePeriod === 'annual' && returnData.frequency !== 'Annual') return null;

                  const returnName = returnKey.replace(/_/g, ' ').toUpperCase();
                  const total = returnData.ytd_on_time + returnData.ytd_late;
                  const onTimeRate = total > 0 ? ((returnData.ytd_on_time / total) * 100).toFixed(0) : '0';

                  return (
                    <tr key={`${inst.institution_id}-${returnKey}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{inst.short_name}</div>
                        <div className="text-xs text-gray-500">{inst.tier}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{returnName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {returnData.frequency}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {returnData.last_submitted || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          returnData.status === 'On Time' ? 'bg-green-100 text-green-800' :
                          returnData.status === 'Late' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {returnData.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm text-gray-900">{onTimeRate}%</div>
                          <div className="ml-2 text-xs text-gray-500">
                            ({returnData.ytd_on_time}/{total})
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Processing Commitments */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Commitments</h3>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <CommitmentCard
              title="Returns Processing"
              commitment="Process within 15 days"
              actual="12 days average"
              status="ahead"
              percentage={120}
            />
            <CommitmentCard
              title="Review Completion"
              commitment="90% reviewed in 30 days"
              actual="94% compliance"
              status="ahead"
              percentage={104}
            />
            <CommitmentCard
              title="Outstanding Reviews"
              commitment="< 50 pending reviews"
              actual="42 pending"
              status="on-track"
              percentage={100}
            />
          </div>
        </div>
      </div>

      {/* Upcoming Deadlines */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Submission Deadlines</h3>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="space-y-3">
            <DeadlineItem
              institution="RBC"
              returnType="M4 Balance Sheet"
              dueDate="2025-11-15"
              daysUntil={23}
              priority="normal"
            />
            <DeadlineItem
              institution="TD"
              returnType="BCAR Quarterly"
              dueDate="2025-11-12"
              daysUntil={20}
              priority="normal"
            />
            <DeadlineItem
              institution="Scotiabank"
              returnType="P3 Income Statement"
              dueDate="2025-11-10"
              daysUntil={18}
              priority="high"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subtitle, icon, color }: any) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
  };

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-600 uppercase">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className={`text-3xl p-3 rounded-lg ${colors[color as keyof typeof colors]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function CommitmentCard({ title, commitment, actual, status, percentage }: any) {
  const statusColors = {
    ahead: 'bg-green-100 text-green-800',
    'on-track': 'bg-blue-100 text-blue-800',
    behind: 'bg-red-100 text-red-800',
  };

  const statusIcons = {
    ahead: '↗️',
    'on-track': '→',
    behind: '↘️',
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
      <div className="space-y-2">
        <div className="text-sm">
          <span className="text-gray-600">Commitment:</span>
          <span className="ml-2 text-gray-900">{commitment}</span>
        </div>
        <div className="text-sm">
          <span className="text-gray-600">Actual:</span>
          <span className="ml-2 font-semibold text-gray-900">{actual}</span>
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors]}`}>
            {statusIcons[status as keyof typeof statusIcons]} {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <span className="text-lg font-bold text-blue-700">{percentage}%</span>
        </div>
      </div>
    </div>
  );
}

function DeadlineItem({ institution, returnType, dueDate, daysUntil, priority }: any) {
  const priorityColors = {
    high: 'border-l-4 border-red-500 bg-red-50',
    normal: 'border-l-4 border-blue-500 bg-blue-50',
    low: 'border-l-4 border-gray-300 bg-gray-50',
  };

  return (
    <div className={`p-3 rounded ${priorityColors[priority as keyof typeof priorityColors]}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <span className="font-semibold text-gray-900">{institution}</span>
            <span className="text-sm text-gray-600">{returnType}</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Due: {dueDate}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-gray-900">{daysUntil}</div>
          <div className="text-xs text-gray-600">days</div>
        </div>
      </div>
    </div>
  );
}
